/* global Paddle */
import { URLs } from "./apis";
import React from "react";

// Check payment success
export const waitForCompletedTransactionStatus = async (transaction_id) => {
  // Unfortunately, payment confirmation from PayPal might take around 10 seconds.
  try {
    const maxAttempts = 15; // Maximum number of attempts (30 seconds)
    let attempts = 0; // Initialize the counter
    while (attempts < maxAttempts) {
      try {
        const response = await fetch(
          URLs.PADDLE_TRANSACTION_STATUS_URL + transaction_id,
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch transaction status: ${response.statusText}`,
          );
        }
        const data = await response.json();
        if (
          data.paddle_status === "completed" &&
          data.subscription_ready === true
        ) {
          await new Promise((resolve) => setTimeout(resolve, 2000)); // Wait for 2 seconds

          return true;
        }
      } catch (error) {
        console.log(error);
      }
      await new Promise((resolve) => setTimeout(resolve, 2000)); // Wait for 2 seconds
      attempts++; // Increment the counter after each attempt
    }
    if (attempts >= maxAttempts) {
      location.href = URLs.SUBSCRIPTION_FAILED_URL + transaction_id;
      return false;
    }
  } catch (error) {
    console.log("Error checking transaction status:", error);
  }
};

// Get current price paddle id and set current plan and period
export const paddleActivePlan = async (
  handleActivePlan,
  handleActivePeriod,
  allPlans,
) => {
  try {
    const response = await fetch(URLs.PADDLE_DETAILS_URL);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();

    // Find the main (non-addon) subscription to set active plan
    // Iterate through all subscriptions and find the one with product_type (Base/Pro)
    if (data && data.length > 0 && allPlans) {
      for (const subscription of data) {
        const subscription_price_paddle_id =
          subscription?.subscription_info?.subscription_price_paddle_id ||
          false;

        const matchingPlan = allPlans.find(
          (plan) => plan?.price?.paddle_id === subscription_price_paddle_id,
        );

        // Only set active plan if it's a main plan (has product_type like Base/Pro)
        if (matchingPlan && matchingPlan.price?.custom_data?.product_type) {
          handleActivePlan(matchingPlan.price.custom_data.product_type);
          handleActivePeriod(matchingPlan.price.billing_cycle_interval);
          break; // Found main plan, stop searching
        }
      }
    }
  } catch (error) {
    console.log(error);
  }
};

// Show window paddle Checkout
export const handleCheckout = (
  priceID,
  userEmail,
  userId,
  subscriberId,
  paddleCustomerId,
  paddleOwnershipToken,
) => {
  const customerInfo = paddleCustomerId
    ? { id: paddleCustomerId }
    : { email: userEmail };

  const items = [
    {
      quantity: 1,
      priceId: priceID,
    },
  ];

  Paddle.Checkout.open({
    items,
    customer: customerInfo,
    customData: {
      email: userEmail,
      user_id: userId,
      subscriber_id: subscriberId,
      ownership_token: paddleOwnershipToken,
    },
  });
};

// Preview subscription quantity update
export const previewSubscriptionUpdate = async (quantity, priceId) => {
  try {
    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch("/paddle/preview-update/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify({ quantity, price_id: priceId }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || "Preview failed");
    }

    return await response.json();
  } catch (error) {
    console.log("Error previewing subscription update:", error);
    throw error;
  }
};

// Update subscription quantity
export const updateSubscriptionQuantity = async (quantity, priceId) => {
  try {
    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch("/paddle/update-quantity/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
      body: JSON.stringify({ quantity, price_id: priceId }),
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.error || "Update failed");
      error.payment_error = data.payment_error || false;
      throw error;
    }

    return data;
  } catch (error) {
    console.log("Error updating subscription quantity:", error);
    throw error;
  }
};

// Global flag to track payment method updates
let isUpdatingPaymentMethod = false;
let paymentMethodUpdateCallback = null;
let checkoutOpenedCallback = null;

// Open Paddle checkout for payment method update
export const openPaymentUpdateCheckout = async (
  onSuccess,
  onCheckoutOpened,
) => {
  try {
    const csrfToken = document.querySelector(
      "[name=csrfmiddlewaretoken]",
    ).value;

    const response = await fetch("/paddle/payment-update-url/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFToken": csrfToken,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to get payment update link");
    }

    const data = await response.json();

    // Set global flag and callbacks
    isUpdatingPaymentMethod = true;
    paymentMethodUpdateCallback = onSuccess;
    checkoutOpenedCallback = onCheckoutOpened;

    Paddle.Checkout.open({
      transactionId: data.transaction_id,
    });
  } catch (error) {
    console.error("Error opening payment update checkout:", error);
    throw error;
  }
};

// Process payment
export const initPaddle = (mode, token, handleLoading) => {
  Paddle.Environment.set(mode);
  Paddle.Initialize({
    token: token,
    checkout: {
      settings: {
        allowLogout: false,
      },
    },
    eventCallback: async function (data) {
      if (data.name === "checkout.loaded") {
        // Call checkout opened callback for payment method updates
        if (isUpdatingPaymentMethod && checkoutOpenedCallback) {
          checkoutOpenedCallback();
          checkoutOpenedCallback = null;
        }
      }

      if (data.name === "checkout.completed") {
        // Check if this is a payment method update
        if (isUpdatingPaymentMethod) {
          isUpdatingPaymentMethod = false;
          Paddle.Checkout.close();

          // Show success modal immediately after Paddle closes
          if (paymentMethodUpdateCallback) {
            paymentMethodUpdateCallback();
            paymentMethodUpdateCallback = null;
          }

          // Don't redirect for payment method updates
          return;
        }

        handleLoading(true);
        Paddle.Checkout.close();
        handleTransaction(data.data.transaction_id);
      }
    },
  });
};

// Check payment
export const handleTransaction = async (transaction_id) => {
  const transactionStatus = await waitForCompletedTransactionStatus(
    transaction_id,
  );

  if (transactionStatus) {
    location.href = URLs.SUBSCRIPTION_SUCCESS_URL + transaction_id;
  } else {
    location.href = URLs.SUBSCRIPTION_FAILED_URL + transaction_id;
  }
};

// Init payment process
export const handleInitPaddle = async (handleLoading) => {
  try {
    const data = await fetchConfigData();

    const { PADDLE_ENVIRONMENT, PADDLE_CLIENT_SIDE_TOKEN } = data;
    initPaddle(PADDLE_ENVIRONMENT, PADDLE_CLIENT_SIDE_TOKEN, handleLoading);
  } catch (error) {
    console.error("Error fetching config data:", error);
  }
};

// Get PADDLE_ENVIRONMENT and PADDLE_CLIENT_SIDE_TOKEN
export const fetchConfigData = async () => {
  try {
    const response = await fetch(URLs.CONFIG_URL);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    const { PADDLE_ENVIRONMENT, PADDLE_CLIENT_SIDE_TOKEN } = data;
    return { PADDLE_ENVIRONMENT, PADDLE_CLIENT_SIDE_TOKEN };
  } catch (error) {
    console.log(error);
    throw error; // In case the caller wants to handle the error
  }
};

// Get Billing plans
export const fetchBillingPlans = async (handleAllPlans) => {
  try {
    const response = await fetch(URLs.PLANS_URL);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();

    handleAllPlans(data);
  } catch (error) {
    console.log(error);
    throw error; // In case the caller wants to handle the error
  }
};

// Button plan texts
export const renderPlanButtonText = (
  product_type,
  activePeriod,
  activePlan,
) => {
  if (!product_type) {
    product_type = "Free";
  }
  if (product_type === "Free") {
    if (activePlan !== "Free") {
      return <>Cancel Subscription</>;
    }
  }

  if (product_type === "Base") {
    if (activePlan === "Base") {
      return activePeriod === "month" ? (
        <>Switch to Annual Plan</>
      ) : (
        <>Switch to Monthly Plan</>
      );
    } else if (activePlan === "Free") {
      return <>Upgrade to Base</>;
    } else if (activePlan === "Pro") {
      return <>Switch to Base</>;
    }
  }

  if (product_type === "Pro") {
    if (activePlan === "Pro") {
      return activePeriod === "month" ? (
        <>Switch to Annual Plan</>
      ) : (
        <>Switch to Monthly Plan</>
      );
    } else {
      return <>Upgrade to Pro</>;
    }
  }

  return null;
};

// Correct text period
export const getPeriodLabel = (activePeriod) => {
  return activePeriod === "month"
    ? "Monthly"
    : activePeriod === "year"
    ? "Yearly"
    : "";
};

// Get subscription details including items and quantity
export const getSubscriptionDetails = async () => {
  try {
    const response = await fetch(URLs.PADDLE_DETAILS_URL);
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const dataList = await response.json();
    // Return all subscriptions, not just the first one
    // Frontend will need to iterate through all to find addon subscriptions
    return dataList || [];
  } catch (error) {
    console.log("Error fetching subscription details:", error);
    return [];
  }
};
